import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaVolumeMute, FaVolumeUp, FaRegImage, FaExpand, FaCompress, FaUndo, FaStepBackward, FaStepForward } from 'react-icons/fa';
import { useTranslation } from 'src/pages';

const EnhancedCameraBox = ({ title, isLive = false, videoSrc = null, isReplay = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const [connectionErrorDetails, setConnectionErrorDetails] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const flvPlayerRef = useRef(null);
  const timeoutRef = useRef(null);
  const firstFrameTimeoutRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const containerRef = useRef(null);
  const initRequestIdRef = useRef(0);
  const maxReconnectDelay = 8000; // 8 secondes max pour le backoff

  // Charger dynamiquement flv.js
  const loadFlvJs = async () => {
    if (window.flvjs) return window.flvjs;
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/flv.js@1.6.2/dist/flv.min.js';
      script.async = true;
      script.onload = () => (window.flvjs ? resolve(window.flvjs) : reject('flv.js not loaded'));
      script.onerror = () => reject('Failed to load flv.js');
      document.head.appendChild(script);
    });
  };

  // Fonction pour nettoyer les timers et événements
  const cleanupTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
    if (firstFrameTimeoutRef.current) {
      clearTimeout(firstFrameTimeoutRef.current);
      firstFrameTimeoutRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const isInterruptedPlayError = (error) => {
    const message = String(error?.message || error || '').toLowerCase();
    return (
      message.includes('interrupted by a new load request') ||
      message.includes('the play() request was interrupted') ||
      message.includes('aborterror')
    );
  };

  // Fonction pour nettoyer le player et les event listeners
  const cleanupPlayer = () => {
    initRequestIdRef.current += 1;

    // Nettoyer les timers
    cleanupTimers();

    // Détacher le player de la vidéo
    if (flvPlayerRef.current) {
      flvPlayerRef.current.detachMediaElement();
      flvPlayerRef.current.destroy();
      flvPlayerRef.current = null;
    }

    // Retirer les event listeners de la vidéo
    if (videoRef.current) {
      videoRef.current.removeEventListener('playing', handleVideoPlaying);
      videoRef.current.removeEventListener('waiting', handleVideoWaiting);
      videoRef.current.removeEventListener('stalled', handleVideoStalled);
      videoRef.current.removeEventListener('error', handleVideoError);
      videoRef.current.removeEventListener('canplay', handleVideoCanPlay);
      videoRef.current.removeEventListener('loadeddata', handleVideoLoadedData);
      videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
    }
  };

  // Fonction pour gérer la reconnexion automatique
  const scheduleReconnect = () => {
    cleanupPlayer();

    // Calculer le délai avec backoff exponentiel
    const delay = Math.min(Math.pow(2, reconnectAttemptsRef.current) * 1000, maxReconnectDelay);

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current += 1;
      initPlayer();
    }, delay);
  };

  // Fonction pour réinitialiser le compteur de tentatives
  const resetReconnectAttempts = () => {
    reconnectAttemptsRef.current = 0;
  };

  const setDetailedError = (message) => {
    setConnectionError(true);
    setConnectionErrorDetails(message);
  };

  // Event handlers pour les événements vidéo
  const handleVideoPlaying = () => {
    setIsLoading(false);
    setIsPlaying(true);
    setConnectionError(false);
    setConnectionErrorDetails('');
    resetReconnectAttempts();

    // Réinitialiser le watchdog
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
    }
    if (firstFrameTimeoutRef.current) {
      clearTimeout(firstFrameTimeoutRef.current);
      firstFrameTimeoutRef.current = null;
    }
  };

  const handleVideoWaiting = () => {
    setIsLoading(true);
  };

  const handleVideoStalled = () => {
    setIsLoading(true);
    // Activer le watchdog pour détecter les freezes
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
    }
    watchdogTimerRef.current = setTimeout(() => {
      console.log('Watchdog: Video appears frozen, attempting recovery...');
      setDetailedError('Video stalled for more than 12 seconds');
      scheduleReconnect();
    }, 12000); // 12 secondes de tolerance avant de considérer comme bloqué
  };

  const handleVideoError = (event) => {
    const mediaError = event?.target?.error;
    const mediaMessage = mediaError
      ? `HTML5 video error code ${mediaError.code}`
      : 'HTML5 video error';
    console.error('Video element error:', mediaError || event);
    setDetailedError(mediaMessage);
    scheduleReconnect();
  };

  const handleVideoCanPlay = () => {
    // Ne pas encore cacher le chargement, attendre playing
  };

  const handleVideoLoadedData = () => {
    if (!videoRef.current) return;
    console.log('Video loadeddata:', {
      src: videoSrc,
      width: videoRef.current.videoWidth,
      height: videoRef.current.videoHeight,
      readyState: videoRef.current.readyState,
    });
    if (videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
      setConnectionErrorDetails('Stream connected but no video frame dimensions yet');
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration || 0);
    }
  };

  // Initialiser le lecteur FLV pour le mode live
  const initFlvPlayer = async () => {
    if (!videoSrc || !videoRef.current) return;

    // Nettoyer lecteur précédent
    cleanupPlayer();
    const requestId = initRequestIdRef.current;

    setConnectionError(false);
    setConnectionErrorDetails('');
    setIsLoading(true);

    // Configurations pour la stabilité
    const playerConfig = {
      enableWorker: true,
      enableStashBuffer: true,
      stashInitialSize: 128, // Valeur raisonnable pour le buffer initial
      autoCleanupSourceBuffer: true,
      autoCleanupMaxBackwardDuration: 90,
      autoCleanupMinBackwardDuration: 60,
    };

    const mediaConfig = {
      hasAudio: true, // Activer le son
      hasVideo: true,
    };

    // Timeout de connexion 20s
    timeoutRef.current = setTimeout(() => {
      console.log('Connexion timeout atteint');
      setDetailedError('Connection timeout after 20 seconds');
      scheduleReconnect();
    }, 20000);

    try {
      const flvjs = await loadFlvJs();
      if (requestId !== initRequestIdRef.current || !videoRef.current) return;
      if (!flvjs.isSupported()) throw new Error('FLV not supported in this browser');

      const player = flvjs.createPlayer({
        type: 'flv',
        url: videoSrc,
        isLive: true,
        cors: true,
        withCredentials: false,
        ...playerConfig,
        ...mediaConfig
      });

      player.attachMediaElement(videoRef.current);
      if (requestId !== initRequestIdRef.current || !videoRef.current) {
        player.detachMediaElement();
        player.destroy();
        return;
      }

      // Ajouter les event listeners à la vidéo
      videoRef.current.addEventListener('playing', handleVideoPlaying);
      videoRef.current.addEventListener('waiting', handleVideoWaiting);
      videoRef.current.addEventListener('stalled', handleVideoStalled);
      videoRef.current.addEventListener('error', handleVideoError);
      videoRef.current.addEventListener('canplay', handleVideoCanPlay);
      videoRef.current.addEventListener('loadeddata', handleVideoLoadedData);
      videoRef.current.addEventListener('timeupdate', handleTimeUpdate);

      player.load();
      firstFrameTimeoutRef.current = setTimeout(() => {
        const video = videoRef.current;
        if (!video) return;

        if (video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
          console.warn('FLV stream opened but no decodable video frame received', {
            src: videoSrc,
            readyState: video.readyState,
            width: video.videoWidth,
            height: video.videoHeight,
          });
          setDetailedError('Stream opened but no decodable video frame was received');
        }
      }, 8000);
      await player.play().catch(err => {
        if (requestId !== initRequestIdRef.current) return;
        if (isInterruptedPlayError(err)) {
          console.warn('Lecture interrompue par une reinitialisation du player:', err);
          return;
        }
        console.error('Erreur de lecture:', err);
        setDetailedError(`Autoplay/play failed: ${err?.message || err}`);
        scheduleReconnect();
      });

      // Gestion des événements FLV
      player.on(flvjs.Events.ERROR, (errType, errDetail) => {
        console.error('FLV Player Error:', errType, errDetail);
        const detailMessage =
          typeof errDetail === 'string'
            ? errDetail
            : errDetail?.msg || JSON.stringify(errDetail || {});

        // Vérifier si c'est une erreur liée à une voiture éteinte (Early-EOF)
        if (errDetail && typeof errDetail === 'object' && errDetail.msg && errDetail.msg.includes('Early-EOF')) {
          // Cas particulier : la voiture est probablement éteinte
          setConnectionError(false); // Ne pas activer l'indicateur d'erreur de connexion
          setConnectionErrorDetails('Camera stream ended early (device may be offline)');
          setIsLoading(false); // Arrêter l'indicateur de chargement
          // Ne pas tenter de reconnexion automatique dans ce cas
        } else {
          setDetailedError(`FLV error: ${errType} ${detailMessage}`);
          scheduleReconnect();
        }
      });

      if (requestId !== initRequestIdRef.current) {
        player.detachMediaElement();
        player.destroy();
        return;
      }

      flvPlayerRef.current = player;
    } catch (err) {
      if (requestId !== initRequestIdRef.current) return;
      if (isInterruptedPlayError(err)) {
        console.warn('Initialisation interrompue par une reinitialisation du player:', err);
        return;
      }
      console.error('FLV Player init error:', err);
      setDetailedError(`FLV init failed: ${err?.message || err}`);
      scheduleReconnect();
    }
  };

  // Initialiser le lecteur vidéo HTML5 pour le mode replay
  const initHtml5Player = () => {
    if (!videoSrc || !videoRef.current) return;

    // Nettoyer le lecteur FLV précédent
    cleanupPlayer();

    setConnectionError(false);
    setConnectionErrorDetails('');
    setIsLoading(true);

    // Charger la vidéo MP4
    videoRef.current.src = videoSrc;
    
    // Ajouter les event listeners à la vidéo
    videoRef.current.addEventListener('playing', handleVideoPlaying);
    videoRef.current.addEventListener('waiting', handleVideoWaiting);
    videoRef.current.addEventListener('stalled', handleVideoStalled);
    videoRef.current.addEventListener('error', handleVideoError);
    videoRef.current.addEventListener('canplay', handleVideoCanPlay);
    videoRef.current.addEventListener('loadeddata', handleVideoLoadedData);
    videoRef.current.addEventListener('timeupdate', handleTimeUpdate);

    // Charger la vidéo
    videoRef.current.load();

    // Démarrer la lecture automatiquement en mode replay
    if (!isLive) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
        setIsLoading(false);
      }).catch(err => {
        console.error('Erreur de lecture MP4:', err);
        setConnectionError(true);
      });
    }
  };

  // Fonction principale pour initialiser le bon lecteur
  const initPlayer = () => {
    if (isReplay || videoSrc?.includes('.mp4') || videoSrc?.includes('firebasestorage.googleapis.com')) {
      // Mode replay - utiliser le lecteur HTML5
      initHtml5Player();
    } else {
      // Mode live - utiliser le lecteur FLV
      initFlvPlayer();
    }
  };

  useEffect(() => {
    // Initialiser le lecteur approprié en fonction du mode
    initPlayer();

    return () => {
      cleanupPlayer();
    };
  }, [videoSrc, isLive, isReplay]);

  const handlePlayPause = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.error('Erreur de lecture:', err);
        setConnectionError(true);
      });
    }
  };

  const handleVolumeToggle = () => setIsMuted(!isMuted);

  const handleScreenshot = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    const link = document.createElement('a');
    link.download = `screenshot-${title}-${Date.now()}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const handleRetry = () => {
    // Réinitialiser les tentatives de reconnexion et réessayer immédiatement
    reconnectAttemptsRef.current = 0;
    initPlayer();
  };

  const handleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        });
      }
    }
  };

  const handleReplay = () => {
    if (videoRef.current && !isLive) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleSkipBackward = () => {
    if (videoRef.current && !isLive) {
      videoRef.current.currentTime -= 10;
    }
  };

  const handleSkipForward = () => {
    if (videoRef.current && !isLive) {
      videoRef.current.currentTime += 10;
    }
  };

  const handleProgressClick = (e) => {
    if (isLive || !videoRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * duration;
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds < 0) return '00:00';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculer la progression pour la barre de progression
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const {t} = useTranslation();
  const debugVideo = videoRef.current;
  const debugInfo = {
    mode: isReplay ? 'replay' : isLive ? 'live' : 'file',
    readyState: debugVideo?.readyState ?? '-',
    networkState: debugVideo?.networkState ?? '-',
    width: debugVideo?.videoWidth ?? 0,
    height: debugVideo?.videoHeight ?? 0,
    currentSrc: debugVideo?.currentSrc || videoSrc || '-',
  };
  
  return (
    <div
      ref={containerRef}
      className="rounded-lg overflow-hidden shadow-md bg-gradient-to-br from-blue-50 to-blue-100 border border-gray-200 rounded-lg relative h-full flex flex-col min-h-0"
    >
      {/* Icône de plein écran en haut à droite */}
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={handleFullscreen}
          className="p-1.5 bg-white bg-opacity-80 rounded-full shadow-md hover:bg-opacity-100 transition-all"
        >
          {isFullscreen ? <FaCompress className="text-gray-700 text-sm" /> : <FaExpand className="text-gray-700 text-sm" />}
        </button>
      </div>

      {/* Contenu principal de la caméra */}
      <div className="flex-1 flex flex-col min-h-0 relative">
        {/* Zone de visualisation avec vidéo ou placeholder */}
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200 relative">

          {/* Loader de connexion en haut à droite */}
          {isLoading && (
            <div className="absolute top-2 right-10 z-10 flex items-center gap-2 bg-white bg-opacity-80 px-3 py-1.5 rounded-full shadow-md">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className="text-xs text-gray-700">Chargement...</span>
            </div>
          )}

          <div className="absolute left-2 top-10 z-10 max-w-[260px] rounded-md bg-black/70 px-3 py-2 text-[11px] text-white shadow-md">
            <div className="font-semibold text-cyan-300">Debug Stream</div>
            <div>Mode: {debugInfo.mode}</div>
            <div>ReadyState: {debugInfo.readyState}</div>
            <div>NetworkState: {debugInfo.networkState}</div>
            <div>Size: {debugInfo.width} x {debugInfo.height}</div>
            <div>Playing: {isPlaying ? 'yes' : 'no'}</div>
            <div>Loading: {isLoading ? 'yes' : 'no'}</div>
            <div>Error: {connectionError ? 'yes' : 'no'}</div>
            <div className="truncate">Src: {debugInfo.currentSrc}</div>
            {connectionErrorDetails && (
              <div className="mt-1 whitespace-normal break-words text-red-300">
                Detail: {connectionErrorDetails}
              </div>
            )}
          </div>

          {connectionError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-100 z-20 p-4">
              <span className="text-red-700 font-medium">
                {t("LEWOOMINING.streamConnectionError")}
              </span>
              {connectionErrorDetails && (
                <span className="mt-2 text-center text-xs text-red-600">
                  {connectionErrorDetails}
                </span>
              )}
              <button
                className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                onClick={handleRetry}
              >
                {t("LEWOOMINING.retry")}
              </button>
            </div>
          )}
          
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted={isMuted}
            autoPlay={isReplay} // Auto-play en mode replay
            controls={!isLive} // Afficher les contrôles en mode replay
          />

          {/* Indicateur Live ou Replay */}
          {(isLive && !isReplay) && (
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-medium z-20">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span> LIVE
            </div>
          )}
          
          {isReplay && (
            <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium z-20">
              <span className="w-1.5 h-1.5 bg-white rounded-full"></span> REPLAY
            </div>
          )}
        </div>

        {/* Barre de contrôle en bas - Toujours visible en superposition */}
        <div
          className="absolute bottom-0 left-0 right-0 bg-white py-2 px-3 flex flex-col border-t border-gray-200 flex-shrink-0 min-h-[60px] opacity-100 transition-opacity duration-300 z-20"
          style={{ pointerEvents: 'auto' }} // S'assurer que les contrôles sont toujours interactifs
        >
          {/* Barre de progression */}
          <div
            className={`w-full h-1.5 rounded-full mb-2 ${isLive ? 'cursor-default' : 'cursor-pointer'}`}
            onClick={isLive ? undefined : handleProgressClick} // Désactiver le clic sur la barre de progression en mode live
          >
            {isLive ? (
              // Indicateur spécial pour le mode live
              <div className="w-full h-full bg-gradient-to-r from-red-500 to-red-700 rounded-full flex items-center justify-end pr-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span className="text-white text-xs ml-1">LIVE</span>
              </div>
            ) : (
              // Barre de progression normale pour les vidéos
              <div
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${progress}%` }} // Afficher la progression en mode replay
              ></div>
            )}
          </div>

          {/* Contrôles */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {/* Icônes de contrôle */}
              <button
                onClick={handleReplay}
                className={`${videoSrc && !isLive ? 'hover:text-blue-600' : 'text-gray-300 cursor-not-allowed'} transition-colors p-1.5`}
                disabled={!videoSrc || isLive}
                title={isLive ? "Non disponible en mode live" : "Recommencer"}
              >
                <FaUndo className="text-base" />
              </button>

              <button
                onClick={handleSkipBackward}
                className={`${videoSrc && !isLive ? 'hover:text-blue-600' : 'text-gray-300 cursor-not-allowed'} transition-colors p-1.5`}
                disabled={!videoSrc || isLive}
                title={isLive ? "Non disponible en mode live" : "Reculer de 10 secondes"}
              >
                <FaStepBackward className="text-base" />
              </button>

              <button
                onClick={handlePlayPause}
                className={`${videoSrc ? 'hover:text-blue-600' : 'text-gray-300 cursor-not-allowed'} transition-colors p-1.5`}
                disabled={!videoSrc}
                title={isLive ? "Lecture/pause non disponible en mode live" : "Lecture/Pause"}
              >
                {isPlaying ? <FaPause className="text-base" /> : <FaPlay className="text-base" />}
              </button>

              <button
                onClick={handleSkipForward}
                className={`${videoSrc && !isLive ? 'hover:text-blue-600' : 'text-gray-300 cursor-not-allowed'} transition-colors p-1.5`}
                disabled={!videoSrc || isLive}
                title={isLive ? "Non disponible en mode live" : "Avancer de 10 secondes"}
              >
                <FaStepForward className="text-base" />
              </button>

              <button
                onClick={handleVolumeToggle}
                className={`${videoSrc ? 'hover:text-blue-600' : 'text-gray-300 cursor-not-allowed'} transition-colors p-1.5`}
                disabled={!videoSrc}
              >
                {isMuted ? <FaVolumeMute className="text-base" /> : <FaVolumeUp className="text-base" />}
              </button>

              <button
                onClick={handleScreenshot}
                className={`${videoSrc ? 'hover:text-blue-600' : 'text-gray-300 cursor-not-allowed'} transition-colors p-1.5`}
                disabled={!videoSrc}
              >
                <FaRegImage className="text-base" />
              </button>

              {/* Affichage du temps */}
              <div className="text-xs text-gray-500 mx-2">
                {isLive ? 'LIVE' : `${formatTime(currentTime)} / ${formatTime(duration)}`}
              </div>
            </div>

            {/* Texte du canal */}
            <div className="font-medium text-gray-700 text-sm mx-1.5 truncate flex-1 text-center">
              {title}
            </div>

            {/* Icône de plein écran */}
            <button
              onClick={handleFullscreen}
              className="text-gray-500 hover:text-blue-600 transition-colors p-1.5"
            >
              {isFullscreen ? <FaCompress className="text-base" /> : <FaExpand className="text-base" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedCameraBox;

